// src/hooks/useAnnouncements.js
// Supplies the Announcements page content.
//
// Reads from Firestore, but falls back to the original hardcoded announcements
// if Firestore is empty or unreachable — the page can never render blank, even
// before the one-time import has been run.

import { useEffect, useMemo, useState } from "react";
import { subscribeToAllAnnouncements } from "../lib/announcementsService";
import { isLive } from "../lib/playersService";
import { STATIC_ANNOUNCEMENTS } from "../data/announcementsData";
import { LEGACY_ANNOUNCEMENT_IMAGES } from "../data/legacyAnnouncementImages";
import { displayableImageUrl } from "../lib/imageUpload";

/** Resolve the image an announcement should display. Shared with the admin. */
export function announcementImage(item) {
  return (
    displayableImageUrl(item.imageUrl, item.imagePath) ||
    LEGACY_ANNOUNCEMENT_IMAGES[item.legacyImageKey] ||
    null
  );
}

function normalise(item, index) {
  return {
    ...item,
    id: item.id || `static-${index}`,
    image: announcementImage(item),
  };
}

/**
 * The live announcements, split by type, drafts and scheduled items excluded.
 * Returns { items, news, tournaments, featured, loading, usingFallback }.
 */
export function useAnnouncements() {
  const [remote, setRemote] = useState(null); // null while loading
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const unsub = subscribeToAllAnnouncements(
      (list) => setRemote(list),
      (err) => {
        console.warn("Announcements fetch failed, using bundled content:", err);
        setFailed(true);
      }
    );
    return unsub;
  }, []);

  const usingFallback = failed || (remote !== null && remote.length === 0);

  const items = useMemo(() => {
    if (usingFallback) {
      return STATIC_ANNOUNCEMENTS.map((item, i) => normalise(item, i));
    }
    if (remote === null) return [];
    // isLive is the same publish test the roster uses (status + publishAt).
    return remote.filter((item) => isLive(item)).map((item, i) => normalise(item, i));
  }, [remote, usingFallback]);

  const news = useMemo(() => items.filter((i) => i.type === "news"), [items]);
  const tournaments = useMemo(() => items.filter((i) => i.type === "tournament"), [items]);
  const featured = useMemo(() => news.find((i) => i.featured) || null, [news]);

  return {
    items,
    news,
    tournaments,
    featured,
    loading: remote === null && !failed,
    usingFallback,
  };
}
